import { useMemo, useState, useCallback } from 'react';

interface SceneRulerProps {
  width: number;
  height: number;
  scale: number;
  children: React.ReactNode;
}

const RULER_SIZE = 24;
const RULER_BG = '#2a2a3c';
const TICK_COLOR = '#888';
const MAJOR_TICK_COLOR = '#aaa';
const LABEL_COLOR = '#ccc';
const BORDER_COLOR = '#444';

function TicksH({ width, scale, hoverX }: { width: number; scale: number; hoverX: number | null }) {
  const ticks = useMemo(() => {
    const arr: { pos: number; kind: 'minor' | 'mid' | 'major' }[] = [];
    for (let i = 0; i <= width; i += 10) {
      const kind = i % 100 === 0 ? 'major' : i % 50 === 0 ? 'mid' : 'minor';
      arr.push({ pos: i, kind });
    }
    return arr;
  }, [width]);

  return (
    <>
      {ticks.map(({ pos, kind }) => {
        const x = pos * scale;
        const y1 = RULER_SIZE;
        const y2 = kind === 'major' ? 4 : kind === 'mid' ? 8 : RULER_SIZE - 6;
        const stroke = kind === 'major' ? MAJOR_TICK_COLOR : TICK_COLOR;
        const sw = kind === 'major' ? 1 : 0.5;
        return (
          <g key={pos}>
            <line x1={x} y1={y1} x2={x} y2={y2} stroke={stroke} strokeWidth={sw} />
            {kind === 'major' && (
              <text x={x + 3} y={RULER_SIZE - 7} fill={LABEL_COLOR} fontSize={9}
                fontFamily="monospace" fontWeight={500}>
                {pos}
              </text>
            )}
          </g>
        );
      })}
      {hoverX !== null && (
        <line
          x1={hoverX * scale} y1={0} x2={hoverX * scale} y2={RULER_SIZE}
          stroke="#ff4d4f" strokeWidth={1} strokeDasharray="3,2"
        />
      )}
    </>
  );
}

function TicksV({ height, scale, hoverY }: { height: number; scale: number; hoverY: number | null }) {
  const ticks = useMemo(() => {
    const arr: { pos: number; kind: 'minor' | 'mid' | 'major' }[] = [];
    for (let i = 0; i <= height; i += 10) {
      const kind = i % 100 === 0 ? 'major' : i % 50 === 0 ? 'mid' : 'minor';
      arr.push({ pos: i, kind });
    }
    return arr;
  }, [height]);

  return (
    <>
      {ticks.map(({ pos, kind }) => {
        const y = pos * scale;
        const x1 = RULER_SIZE;
        const x2 = kind === 'major' ? 4 : kind === 'mid' ? 8 : RULER_SIZE - 6;
        const stroke = kind === 'major' ? MAJOR_TICK_COLOR : TICK_COLOR;
        const sw = kind === 'major' ? 1 : 0.5;
        return (
          <g key={pos}>
            <line x1={x1} y1={y} x2={x2} y2={y} stroke={stroke} strokeWidth={sw} />
            {kind === 'major' && (
              <text x={RULER_SIZE - 7} y={y + 3} fill={LABEL_COLOR} fontSize={9}
                fontFamily="monospace" fontWeight={500}
                transform={`rotate(-90, ${RULER_SIZE - 7}, ${y + 3})`}>
                {pos}
              </text>
            )}
          </g>
        );
      })}
      {hoverY !== null && (
        <line
          x1={0} y1={hoverY * scale} x2={RULER_SIZE} y2={hoverY * scale}
          stroke="#ff4d4f" strokeWidth={1} strokeDasharray="3,2"
        />
      )}
    </>
  );
}

export default function SceneRuler({ width, height, scale, children }: SceneRulerProps) {
  // 宽屏自适应已回退(2026-08-19)：ResizeObserver 跟随容器尺寸疑似触发重渲染抖动导致界面卡顿
  // 恢复原始固定设计稿坐标(宽度/高度/scale 必填)逻辑
  const svgW = width * scale;
  const svgH = height * scale;
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [hoverY, setHoverY] = useState<number | null>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    setHoverX(x >= 0 && x <= width ? x : null);
    setHoverY(y >= 0 && y <= height ? y : null);
  }, [scale, width, height]);

  const handleMouseLeave = useCallback(() => {
    setHoverX(null);
    setHoverY(null);
  }, []);

  return (
    <div className="editor-ruler" style={{ display: 'flex', flexDirection: 'column', height: '100%', userSelect: 'none' }}>
      {/* Row 1: corner + top ruler */}
      <div style={{ display: 'flex' }}>
        <div className="editor-ruler-corner" style={{ position: 'relative' }}>
          {hoverX !== null && hoverY !== null && (
            <div style={{
              position: 'absolute', top: 2, left: 2, fontSize: 9, color: '#ff4d4f',
              fontFamily: 'monospace', whiteSpace: 'nowrap', background: 'rgba(0,0,0,0.6)',
              padding: '0 4px', borderRadius: 3, zIndex: 10,
            }}>
              ({Math.round(hoverX)}, {Math.round(hoverY)})
            </div>
          )}
        </div>
        <div className="editor-ruler-horizontal">
          <svg width={svgW} height={RULER_SIZE} style={{ display: 'block' }}>
            <TicksH width={width} scale={scale} hoverX={hoverX} />
          </svg>
        </div>
      </div>

      {/* Row 2: left ruler + content */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div className="editor-ruler-vertical">
          <svg width={RULER_SIZE} height={svgH} style={{ display: 'block' }}>
            <TicksV height={height} scale={scale} hoverY={hoverY} />
          </svg>
        </div>

        {/* Content area (canvas) */}
        <div
          style={{ flex: 1, position: 'relative', overflow: 'hidden' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
